// Chakra imports
import { Portal, Box, useDisclosure } from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin.js';
import WelcomePortalModal from 'components/modals/WelcomePortalModal';
// Layout components
import Navbar from 'components/navbar/NavbarAdmin.js';
import Sidebar from 'components/sidebar/Sidebar.js';
import { SidebarContext } from 'contexts/SidebarContext';
import { useAuth } from 'contexts/AuthContext';
import React, { useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import routes from 'routes.js';
import { filterRoutesByPermissions, firstAllowedAdminPath } from 'constants/permissions';
import { transformDoctorSidebarRoutes } from 'utils/doctorSidebarRoutes';
import RequirePermission from 'components/auth/RequirePermission';
import { AttendanceNfcProvider } from 'contexts/AttendanceNfcContext';
import { PatientNavbarProvider } from 'contexts/PatientNavbarContext';

// Custom Chakra theme
export default function Dashboard(props) {
  const { ...rest } = props;
  const { user } = useAuth();
  const location = useLocation();
  const welcomeName = user?.name ?? user?.full_name ?? user?.email ?? '';
  const allowedRoutes = React.useMemo(
    () => transformDoctorSidebarRoutes(filterRoutesByPermissions(routes, user), user),
    [user]
  );
  const defaultAdminPath = React.useMemo(() => firstAllowedAdminPath(user, routes), [user]);
  // states and functions
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  // functions for changing the states from components
  const getRoute = () => {
    return window.location.pathname !== '/admin/full-screen-maps';
  };
  const getActiveRoute = (routeList) => {
    const pathname = location.pathname || '';
    let bestName = 'NIMSI';
    let bestScore = -1;

    const walk = (list) => {
      (list || []).forEach((route) => {
        if (route?.items?.length) walk(route.items);
        if (!route?.layout || route.path == null) return;
        const full = `${route.layout}${route.path}`.replace(/\/$/, '') || route.layout;
        const pattern = full.replace(/:[^/]+/g, '[^/]+');
        const matched = new RegExp(`^${pattern}(?:/|$)`).test(pathname);
        if (matched && full.length > bestScore) {
          bestName = route.name || 'NIMSI';
          bestScore = full.length;
        }
      });
    };

    walk(routeList);
    return bestName;
  };
  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbar(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };
  const getActiveNavbarText = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbarText(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbarText(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].messageNavbar;
        }
      }
    }
    return activeNavbar;
  };
  const getRoutes = (routeList) => {
    return routeList.flatMap((route, key) => {
      if ((route.category || route.collapse) && route.items) {
        return getRoutes(route.items);
      }
      if (route.layout === '/admin' && route.component) {
        return (
          <Route
            path={`${route.path}`}
            element={<RequirePermission>{route.component}</RequirePermission>}
            key={route.path || key}
          />
        );
      }
      return [];
    });
  };
  document.documentElement.dir = 'ltr';
  const { onOpen } = useDisclosure();
  document.documentElement.dir = 'ltr';
  return (
    <Box>
      <Box>
        <SidebarContext.Provider
          value={{
            toggleSidebar,
            setToggleSidebar,
          }}
        >
          <AttendanceNfcProvider>
          <PatientNavbarProvider>
          <WelcomePortalModal userName={welcomeName} />
          <Sidebar routes={allowedRoutes} display="none" {...rest} />
          <Box
            float="right"
            minHeight="100vh"
            height="100%"
            overflow="auto"
            position="relative"
            maxHeight="100%"
            w={{ base: '100%', xl: 'calc( 100% - 290px )' }}
            maxWidth={{ base: '100%', xl: 'calc( 100% - 290px )' }}
            transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
            transitionDuration=".2s, .2s, .35s"
            transitionProperty="top, bottom, width"
            transitionTimingFunction="linear, linear, ease"
          >
            <Portal>
              <Box>
                <Navbar
                  onOpen={onOpen}
                  logoText={'NIMSI Clinic'}
                  brandText={getActiveRoute(routes)}
                  secondary={getActiveNavbar(routes)}
                  message={getActiveNavbarText(routes)}
                  fixed={fixed}
                  {...rest}
                />
              </Box>
            </Portal>

            {getRoute() ? (
              <Box
                mx="auto"
                p={{ base: '20px', md: '30px' }}
                pe="20px"
                minH="100vh"
                pt="50px"
              >
                <Routes>
                  {getRoutes(allowedRoutes)}
                  <Route
                    path="/"
                    element={<Navigate to={defaultAdminPath} replace />}
                  />
                </Routes>
              </Box>
            ) : null}
            <Box>
              <Footer />
            </Box>
          </Box>
          </PatientNavbarProvider>
          </AttendanceNfcProvider>
        </SidebarContext.Provider>
      </Box>
    </Box>
  );
}
